import { Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';

import { queryClient } from 'services/http/queryClient';
import { FEATURE_FLAGS } from 'support/helpers/featureFlags/featureFlags';
import {
  AuthRoutes,
  ConnectionsRoutes,
  DashboardRoutes,
  InboxRoutes,
  InternalRoutes,
  InvoiceVerificationRoutes,
  MetabaseRoutes,
  NetworkRoutes,
  OrderFormsRoutes,
  PaymentReconciliationRoutes,
  PriceCatalogRoutes,
  PricingConfigRoutes,
  SettingsRoutes,
  TransactionsRoutes,
} from 'support/types';
import { CreateAccount } from 'pages/Auth/CreateAccount/CreateAccount';
import { ExpiredLink } from 'pages/Auth/ExpiredLink/ExpiredLink';
import { ForgotPassword } from 'pages/Auth/ForgotPassword/ForgotPassword';
import { Login } from 'pages/Auth/Login/Login';
import { OrderFormLogin } from 'pages/Auth/OrderFormLogin/OrderFormLogin';
import { OrderFormUnauthorized } from 'pages/Auth/OrderFormUnauthorized/OrderFormUnauthorized';
import { PylonLogin } from 'pages/Auth/PylonLogin/PylonLogin';
import { Register } from 'pages/Auth/Register/Register';
import { ResetPassword } from 'pages/Auth/ResetPassword/ResetPassword';
import { ConnectionDetailsPage } from 'pages/Connections/ConnectionDetailsPage/ConnectionDetailsPage';
import { RelationshipDetailsPage } from 'pages/Connections/ConnectionDetailsPage/RelationshipDetailsPage/RelationshipDetailsPage';
import { Connections } from 'pages/Connections/Connections';
import { legacyRelationshipRedirectLoader } from 'pages/Connections/legacyRelationshipRedirectLoader';
import { Dashboard } from 'pages/Dashboard/Dashboard';
import { Inbox } from 'pages/Inbox/Inbox';
import { InvoiceVerification } from 'pages/InvoiceVerification/InvoiceVerification';
import { InvoiceVerificationDetails } from 'pages/InvoiceVerification/InvoiceVerificationDetails/InvoiceVerificationDetails';
import { Metabase } from 'pages/Metabase/Metabase';
import { OrderForm } from 'pages/OrderForm/OrderForm';
import { OrderFormCheckout } from 'pages/OrderFormCheckout/OrderFormCheckout';
import { OrderFormsPitch } from 'pages/OrderFormsPitch/OrderFormsPitch';
import { OrderFormSuccess } from 'pages/OrderFormSuccess/OrderFormSuccess';
import { PaymentReconciliation } from 'pages/PaymentReconciliation/PaymentReconciliation';
import { PaymentReconciliationDetails } from 'pages/PaymentReconciliation/PaymentReconciliationDetails/PaymentReconciliationDetails';
import { PaymentReconciliationPitch } from 'pages/PaymentReconciliation/PaymentReconciliationPitch';
import { PriceCatalogsPage } from 'pages/PriceCatalogs/PriceCatalogsPage';
import { EditPricingConfigsPage } from 'pages/PriceConfigs/EditPricingConfigs';
import { NewPricingConfigsPage } from 'pages/PriceConfigs/NewPricingConfigs';
import { PricingConfigsPage } from 'pages/PriceConfigs/PricingConfigsPage';
import { CompanyData } from 'pages/Settings/CompanyData/CompanyData';
import { Password } from 'pages/Settings/Password/Password';
import { ProfileAndPreferences } from 'pages/Settings/ProfileAndPreferences/ProfileAndPreferences';
import { TeamMembers } from 'pages/Settings/TeamMembers/TeamMembers';
import { TasksPreview } from 'pages/TasksPreview/TasksPreview';
import { TradePartners } from 'pages/TradePartners/TradePartners';
import { BlockedTransactions } from 'pages/Transactions/BlockedTransactions/BlockedTransactions';
import { CreateDocumentFromSource } from 'pages/Transactions/CreateDocumentFromSource/CreateDocumentFromSource';
import { CreateDocumentStandalone } from 'pages/Transactions/CreateDocumentStandalone/CreateDocumentStandalone';
import { EditDocument } from 'pages/Transactions/EditDocument/EditDocument';
import { ExpiredTransactions } from 'pages/Transactions/ExpiredTransactions/ExpiredTransactions';
import { Transactions } from 'pages/Transactions/Transactions';
import { MessageViewPage } from 'pages/Transactions/TransactionViewPage/MessageViewPage/MessageViewPage';
import { SenderEnvelopeViewPage } from 'pages/Transactions/TransactionViewPage/SenderEnvelopeView/SenderEnvelopeViewPage';
import { FeatureFlagGuard } from 'components/FeatureFlagGuard/FeatureFlagGuard';
import { MainErrorBoundary } from 'components/MainErrorBoundary/MainErrorBoundary';
import { OrderFormRoute } from 'components/OrderFormRoute/OrderFormRoute';
import { NotFoundPage } from 'components/Page/NotFoundPage';
import { Page } from 'components/Page/Page';
import { PartnerFeatureFlagGuard } from 'components/PartnerFeatureFlagGuard/PartnerFeatureFlagGuard';
import { PrivateRoute } from 'components/PrivateRoute/PrivateRoute';

import Root from './Root';

/**
 * Extracts a relative path from an absolute route by removing the parent path prefix.
 * Used for nested routes where React Router requires relative paths for children.
 */
const getRelativePath = (absolutePath: string, parentPath: string): string => {
  return absolutePath.replace(parentPath + '/', '');
};

export const router = createBrowserRouter(
  [
    {
      element: <Root />,
      path: DashboardRoutes.root,
      errorElement: <MainErrorBoundary />,
      children: [
        {
          path: '*',
          element: <NotFoundPage />,
        },
        {
          path: DashboardRoutes.legacyRoot,
          element: <Navigate to={DashboardRoutes.root} replace />,
        },
        {
          path: SettingsRoutes.root,
          element: <Navigate to={SettingsRoutes.companyData} replace />,
        },
        {
          path: AuthRoutes.login,
          element: <Login />,
        },
        {
          path: AuthRoutes.register,
          element: <Register />,
        },
        {
          path: AuthRoutes.forgotPassword,
          element: <ForgotPassword />,
        },
        {
          path: AuthRoutes.resetPassword,
          element: <ResetPassword />,
        },
        {
          path: AuthRoutes.createAccount,
          element: <CreateAccount />,
        },
        {
          path: AuthRoutes.expiredLink,
          element: <ExpiredLink />,
        },
        {
          path: AuthRoutes.pylonLogin,
          element: (
            <PrivateRoute>
              <PylonLogin />
            </PrivateRoute>
          ),
        },
        {
          index: true,
          element: (
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          ),
        },
        {
          path: InboxRoutes.root,
          handle: Inbox.handle,
          element: (
            <PrivateRoute>
              <Inbox />
            </PrivateRoute>
          ),
        },
        {
          path: NetworkRoutes.deprecatedRoot,
          element: <Navigate to={NetworkRoutes.root} replace />,
        },
        {
          path: NetworkRoutes.root,
          element: (
            <PrivateRoute>
              <Outlet />
            </PrivateRoute>
          ),
          handle: TradePartners.handle,
          children: [
            {
              index: true,
              element: <TradePartners />,
            },
            {
              path: NetworkRoutes.connectionDetails,
              element: <Outlet />,
              handle: ConnectionDetailsPage.handleFromNetwork,
              loader: ConnectionDetailsPage.loaderFromNetwork(queryClient),
              children: [
                {
                  index: true,
                  element: <ConnectionDetailsPage origin="network" />,
                },
                {
                  path: getRelativePath(NetworkRoutes.testRelationshipMessageDetails, NetworkRoutes.connectionDetails),
                  element: <RelationshipDetailsPage origin="network" />,
                  handle: RelationshipDetailsPage.handleFromNetwork,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(
                    NetworkRoutes.testRelationshipTransactionError,
                    NetworkRoutes.connectionDetails,
                  ),
                  element: <RelationshipDetailsPage origin="network" />,
                  handle: RelationshipDetailsPage.handleFromNetwork,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(NetworkRoutes.testRelationship, NetworkRoutes.connectionDetails),
                  element: <RelationshipDetailsPage origin="network" />,
                  handle: RelationshipDetailsPage.handleFromNetwork,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(NetworkRoutes.goLiveRelationship, NetworkRoutes.connectionDetails),
                  element: <RelationshipDetailsPage origin="network" />,
                  handle: RelationshipDetailsPage.handleFromNetwork,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(NetworkRoutes.taskDetails, NetworkRoutes.connectionDetails),
                  element: <RelationshipDetailsPage origin="network" />,
                  handle: RelationshipDetailsPage.handleFromNetwork,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(NetworkRoutes.relationshipDetails, NetworkRoutes.connectionDetails),
                  element: <RelationshipDetailsPage origin="network" />,
                  handle: RelationshipDetailsPage.handleFromNetwork,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
              ],
            },
            {
              path: NetworkRoutes.deprecatedRelationshipDetails,
              loader: legacyRelationshipRedirectLoader(queryClient, 'network'),
            },
          ],
        },
        {
          path: MetabaseRoutes.root,
          element: (
            <PrivateRoute>
              <Metabase />
            </PrivateRoute>
          ),
        },
        {
          path: PriceCatalogRoutes.root,
          element: (
            <PrivateRoute>
              <PartnerFeatureFlagGuard
                to={DashboardRoutes.root}
                featureFlags={[FEATURE_FLAGS.TEMPORARY_PRICE_CATALOGS]}
              >
                <Outlet />
              </PartnerFeatureFlagGuard>
            </PrivateRoute>
          ),
          handle: PriceCatalogsPage.handle,
          children: [
            {
              index: true,
              element: <PriceCatalogsPage />,
            },
            {
              path: PriceCatalogRoutes.relationshipDetails,
              element: <PriceCatalogsPage />,
            },
            {
              path: PriceCatalogRoutes.viewMessage,
              handle: MessageViewPage.handle,
              loader: MessageViewPage.loader(queryClient),
              element: <MessageViewPage />,
            },
            {
              handle: SenderEnvelopeViewPage.handle,
              loader: SenderEnvelopeViewPage.loader(queryClient),
              path: PriceCatalogRoutes.viewSenderEnvelope,
              element: <SenderEnvelopeViewPage />,
            },
            {
              path: PriceCatalogRoutes.create,
              element: <CreateDocumentStandalone isProductCatalogRoute={true} />,
              loader: CreateDocumentStandalone.loader(queryClient),
              handle: CreateDocumentStandalone.handle,
            },
          ],
        },
        {
          path: PricingConfigRoutes.root,
          element: (
            <PrivateRoute>
              <Outlet />
            </PrivateRoute>
          ),
          handle: PricingConfigsPage.handle,
          children: [
            {
              index: true,
              element: <PricingConfigsPage />,
            },
            {
              path: PricingConfigRoutes.view,
              element: <EditPricingConfigsPage />,
              loader: EditPricingConfigsPage.loader(queryClient),
              handle: EditPricingConfigsPage.handle,
            },
            {
              path: PricingConfigRoutes.new,
              element: <NewPricingConfigsPage />,
              handle: NewPricingConfigsPage.handle,
            },
          ],
        },

        {
          path: PaymentReconciliationRoutes.root,
          element: (
            <PrivateRoute>
              <FeatureFlagGuard featureFlag={FEATURE_FLAGS.RECONCILIATION} to={DashboardRoutes.root}>
                <Outlet />
              </FeatureFlagGuard>
            </PrivateRoute>
          ),
          handle: PaymentReconciliation.handle,
          children: [
            {
              index: true,
              element: <PaymentReconciliation />,
            },
            {
              path: PaymentReconciliationRoutes.detail,
              element: <PaymentReconciliationDetails />,
              handle: PaymentReconciliationDetails.handle,
              loader: PaymentReconciliationDetails.loader(queryClient),
            },
          ],
        },
        {
          path: TransactionsRoutes.root,
          element: (
            <PrivateRoute>
              <Outlet />
            </PrivateRoute>
          ),
          handle: Transactions.handle,
          children: [
            {
              index: true,
              element: <Transactions />,
            },
            {
              path: TransactionsRoutes.viewSenderEnvelope,
              element: <SenderEnvelopeViewPage />,
              loader: SenderEnvelopeViewPage.loader(queryClient),
              handle: SenderEnvelopeViewPage.handle,
            },
            {
              path: TransactionsRoutes.createStandalone,
              element: <CreateDocumentStandalone isProductCatalogRoute={false} />,
              loader: CreateDocumentStandalone.loader(queryClient),
              handle: CreateDocumentStandalone.handle,
            },
            {
              path: TransactionsRoutes.viewMessage,
              element: <Outlet />,
              loader: MessageViewPage.loader(queryClient),
              handle: MessageViewPage.handle,
              children: [
                {
                  index: true,
                  element: <MessageViewPage />,
                },
                {
                  path: TransactionsRoutes.create,
                  element: <CreateDocumentFromSource />,
                  handle: CreateDocumentFromSource.handle,
                },
                {
                  path: TransactionsRoutes.edit,
                  element: <EditDocument />,
                  loader: EditDocument.loader(queryClient),
                  handle: EditDocument.handle,
                },
              ],
            },
          ],
        },
        {
          path: TransactionsRoutes.blocked,
          element: (
            <PrivateRoute>
              <BlockedTransactions />
            </PrivateRoute>
          ),
          handle: BlockedTransactions.handle,
        },
        {
          path: TransactionsRoutes.expired,
          element: (
            <PrivateRoute>
              <ExpiredTransactions />
            </PrivateRoute>
          ),
          handle: ExpiredTransactions.handle,
        },
        {
          path: ConnectionsRoutes.root,
          element: (
            <PrivateRoute>
              <Outlet />
            </PrivateRoute>
          ),
          handle: Connections.handle,
          children: [
            {
              index: true,
              element: <Connections />,
            },
            {
              path: ConnectionsRoutes.connectionDetails,
              element: <Outlet />,
              handle: ConnectionDetailsPage.handle,
              loader: ConnectionDetailsPage.loader(queryClient),
              children: [
                {
                  index: true,
                  element: <ConnectionDetailsPage origin="connections" />,
                },
                {
                  path: getRelativePath(
                    ConnectionsRoutes.testRelationshipMessageDetails,
                    ConnectionsRoutes.connectionDetails,
                  ),
                  element: <RelationshipDetailsPage origin="connections" />,
                  handle: RelationshipDetailsPage.handle,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(
                    ConnectionsRoutes.testRelationshipTransactionError,
                    ConnectionsRoutes.connectionDetails,
                  ),
                  element: <RelationshipDetailsPage origin="connections" />,
                  handle: RelationshipDetailsPage.handle,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(ConnectionsRoutes.testRelationship, ConnectionsRoutes.connectionDetails),
                  element: <RelationshipDetailsPage origin="connections" />,
                  handle: RelationshipDetailsPage.handle,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(ConnectionsRoutes.goLiveRelationship, ConnectionsRoutes.connectionDetails),
                  element: <RelationshipDetailsPage origin="connections" />,
                  handle: RelationshipDetailsPage.handle,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(ConnectionsRoutes.taskDetails, ConnectionsRoutes.connectionDetails),
                  element: <RelationshipDetailsPage origin="connections" />,
                  handle: RelationshipDetailsPage.handle,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
                {
                  path: getRelativePath(ConnectionsRoutes.relationshipDetails, ConnectionsRoutes.connectionDetails),
                  element: <RelationshipDetailsPage origin="connections" />,
                  handle: RelationshipDetailsPage.handle,
                  loader: RelationshipDetailsPage.loader(queryClient),
                },
              ],
            },
            {
              path: ConnectionsRoutes.deprecatedRelationshipDetails,
              loader: legacyRelationshipRedirectLoader(queryClient, 'connections'),
            },
          ],
        },
        {
          path: SettingsRoutes.companyData,
          element: (
            <PrivateRoute>
              <CompanyData />
            </PrivateRoute>
          ),
        },
        {
          path: SettingsRoutes.teamMembers,
          element: (
            <PrivateRoute>
              <TeamMembers />
            </PrivateRoute>
          ),
        },
        {
          path: SettingsRoutes.profileAndPreferences,
          element: (
            <PrivateRoute>
              <ProfileAndPreferences />
            </PrivateRoute>
          ),
        },
        {
          path: SettingsRoutes.password,
          element: (
            <PrivateRoute>
              <Password />
            </PrivateRoute>
          ),
        },
        {
          path: InvoiceVerificationRoutes.root,
          element: (
            <PrivateRoute>
              <FeatureFlagGuard featureFlag={FEATURE_FLAGS.INVOICE_VERIFICATION} to={DashboardRoutes.root}>
                <Outlet />
              </FeatureFlagGuard>
            </PrivateRoute>
          ),
          handle: InvoiceVerification.handle,
          children: [
            {
              index: true,
              element: <InvoiceVerification />,
            },
            {
              path: InvoiceVerificationRoutes.detail,
              element: (
                <Suspense fallback={<Page isLoading>{null}</Page>}>
                  <InvoiceVerificationDetails />
                </Suspense>
              ),
              loader: InvoiceVerificationDetails.loader(queryClient),
              handle: InvoiceVerificationDetails.handle,
            },
          ],
        },
        {
          path: OrderFormsRoutes.pitch,
          element: (
            <PrivateRoute>
              <FeatureFlagGuard featureFlag={FEATURE_FLAGS.ORDER_FORMS_PITCH} to={DashboardRoutes.root}>
                <OrderFormsPitch />
              </FeatureFlagGuard>
            </PrivateRoute>
          ),
          handle: OrderFormsPitch.handle,
        },
        {
          path: OrderFormsRoutes.root,
          element: (
            <OrderFormRoute>
              <OrderForm />
            </OrderFormRoute>
          ),
        },
        {
          path: OrderFormsRoutes.checkout,
          loader: OrderFormCheckout.loader,
          element: (
            <OrderFormRoute>
              <OrderFormCheckout />
            </OrderFormRoute>
          ),
        },
        {
          path: OrderFormsRoutes.unauthorized,
          element: <OrderFormUnauthorized />,
        },
        {
          path: OrderFormsRoutes.login,
          element: <OrderFormLogin />,
        },
        {
          path: OrderFormsRoutes.successMessage,
          element: <OrderFormSuccess type="message" />,
        },
        {
          path: OrderFormsRoutes.successSenderEnvelope,
          element: <OrderFormSuccess type="sender-envelope" />,
        },
        {
          path: PaymentReconciliationRoutes.pitch,
          element: (
            <PrivateRoute>
              <FeatureFlagGuard featureFlag={FEATURE_FLAGS.RECONCILIATION_PITCH} to={DashboardRoutes.root}>
                <PaymentReconciliationPitch />
              </FeatureFlagGuard>
            </PrivateRoute>
          ),
          handle: PaymentReconciliationPitch.handle,
        },
        {
          element: <Outlet />,
          children: [
            { path: InternalRoutes.tasksPreview, element: <TasksPreview /> },
            { path: InternalRoutes.tasksPreviewTask, element: <TasksPreview /> },
          ],
        },
      ],
    },
  ],
  {
    future: {
      // Normalize `useNavigation()`/`useFetcher()` `formMethod` to uppercase
      v7_normalizeFormMethod: true,
    },
  },
);
